import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { Navigate } from 'react-router-dom'
import QRCode from 'qrcode'
import { ApiError } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import { useI18n } from '../i18n/I18nProvider'
import { LanguageToggle } from '../components/LanguageToggle'
import { Gerbe } from '../components/Marque'
import { adminApi } from './adminApi'
import type { AdminChallenge, AdminSession } from './adminApi'

type Etape =
  | { nom: 'motDePasse' }
  | { nom: 'enrolement'; defi: AdminChallenge }
  | { nom: 'verification'; defi: AdminChallenge }
  | { nom: 'secours'; resultat: AdminSession }

/** La clé Base32 par groupes de quatre : elle se recopie sans perdre sa place. */
function grouper(cle: string): string {
  return cle.replace(/(.{4})/g, '$1 ').trim()
}

/**
 * Connexion au portail d'administration : mot de passe, PUIS second facteur.
 *
 * Premier passage : le serveur rend un secret neuf, montré ici en code QR et en clair,
 * UNE fois ; le premier code juste confirme l'enrôlement et rend dix codes de secours, eux
 * aussi montrés une seule fois. La session n'est adoptée qu'après que l'administrateur a dit
 * les avoir conservés — l'adopter avant ferait quitter l'écran qui les affiche.
 *
 * Les passages suivants demandent un code, ou un code de secours. Toute décision est
 * serveur : cet écran ne vérifie rien qu'il puisse vérifier à tort.
 */
export function AdminLoginPage() {
  const { t, translateError } = useI18n()
  const { session, adopt } = useAuth()

  const [etape, setEtape] = useState<Etape>({ nom: 'motDePasse' })
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [parSecours, setParSecours] = useState(false)
  const [qr, setQr] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)
  const [copie, setCopie] = useState(false)

  const uri = etape.nom === 'enrolement' ? etape.defi.otpAuthUri : null

  useEffect(() => {
    if (uri === null) {
      setQr(null)
      return
    }
    let actif = true
    QRCode.toDataURL(uri, { margin: 1, width: 200, errorCorrectionLevel: 'M' })
      .then((url) => actif && setQr(url))
      .catch(() => actif && setQr(null))
    return () => {
      actif = false
    }
  }, [uri])

  // Aucune session avant « Je les ai conservés » : l'écran des codes reste donc affiché.
  if (session !== null) {
    return <Navigate to="/platform" replace />
  }

  function echec(cause: unknown) {
    setError(cause instanceof ApiError ? translateError(cause.code, cause.message) : translateError(undefined))

    // Étape expirée : retour au mot de passe, rien d'autre ne peut la ranimer.
    if (cause instanceof ApiError && cause.code === 'auth.challenge_invalid') {
      recommencer(false)
    }
  }

  function recommencer(effacerErreur = true) {
    setEtape({ nom: 'motDePasse' })
    setCode('')
    setPassword('')
    setParSecours(false)
    if (effacerErreur) setError(null)
  }

  async function envoyer(action: () => Promise<void>) {
    setError(null)
    setPending(true)
    try {
      await action()
    } catch (cause) {
      echec(cause)
    } finally {
      setPending(false)
    }
  }

  function soumettreMotDePasse(event: FormEvent) {
    event.preventDefault()
    void envoyer(async () => {
      const defi = await adminApi.signIn(email, password)
      setPassword('')
      setCode('')
      setEtape(defi.step === 'enroll' ? { nom: 'enrolement', defi } : { nom: 'verification', defi })
    })
  }

  function soumettreCode(event: FormEvent) {
    event.preventDefault()
    if (etape.nom === 'enrolement') {
      void envoyer(async () => {
        const resultat = await adminApi.enroll(etape.defi.challengeToken, code)
        setEtape({ nom: 'secours', resultat })
      })
    } else if (etape.nom === 'verification') {
      void envoyer(async () => {
        const resultat = await adminApi.verify(
          etape.defi.challengeToken,
          parSecours ? { recoveryCode: code } : { code },
        )
        adopt(resultat.session)
      })
    }
  }

  async function copier(codes: string[]) {
    try {
      await navigator.clipboard.writeText(codes.join('\n'))
      setCopie(true)
    } catch {
      setCopie(false)
    }
  }

  const erreur = error !== null && (
    <p className="banner" style={{ background: 'var(--crit-bg)', borderColor: 'color-mix(in srgb,var(--crit) 30%,transparent)' }} role="alert">
      {error}
    </p>
  )

  const ltr = { direction: 'ltr' as const, unicodeBidi: 'isolate' as const, fontFamily: 'ui-monospace, monospace' }

  return (
    <div className="login-wrap">
      <div className="login">
        <div style={{ textAlign: 'center', marginBottom: 16, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <Gerbe taille={40} style={{ color: 'var(--brand, #1F7A5C)' }} />
          <h1 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>{t('admin.title')}</h1>
          <p className="mini" style={{ margin: 0, color: 'var(--muted)' }}>{t('admin.login.subtitle')}</p>
        </div>

        <section className="card">
          <div className="body" style={{ display: 'grid', gap: 11 }}>
            {etape.nom === 'motDePasse' && (
              <form onSubmit={soumettreMotDePasse} style={{ display: 'grid', gap: 11 }}>
                <h3 style={{ margin: 0, fontSize: 13.5, fontWeight: 600 }}>{t('login.title')}</h3>
                <div className="field">
                  <label htmlFor="email">{t('login.email')}</label>
                  <input id="email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <div className="field">
                  <label htmlFor="password">{t('login.password')}</label>
                  <input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
                </div>
                {erreur}
                <button type="submit" disabled={pending} className="btn" style={{ justifyContent: 'center', width: '100%' }}>
                  {pending ? t('login.pending') : t('admin.login.continue')}
                </button>
              </form>
            )}

            {(etape.nom === 'enrolement' || etape.nom === 'verification') && (
              <form onSubmit={soumettreCode} style={{ display: 'grid', gap: 11 }}>
                <h3 style={{ margin: 0, fontSize: 13.5, fontWeight: 600 }}>
                  {t(etape.nom === 'enrolement' ? 'admin.enroll.title' : 'admin.verify.title')}
                </h3>
                <p className="mini" style={{ margin: 0 }}>
                  {t(etape.nom === 'enrolement' ? 'admin.enroll.intro' : 'admin.verify.intro')}
                </p>

                {etape.nom === 'enrolement' && (
                  <>
                    {qr !== null && (
                      <img src={qr} alt={t('admin.enroll.qrAlt')} width={200} height={200} style={{ justifySelf: 'center', background: '#fff', padding: 6, borderRadius: 6 }} />
                    )}
                    <div className="field">
                      <label>{t('admin.enroll.manual')}</label>
                      <code data-testid="cle-manuelle" style={{ ...ltr, fontSize: 13, userSelect: 'all', wordBreak: 'break-all' }}>
                        {grouper(etape.defi.secret ?? '')}
                      </code>
                    </div>
                  </>
                )}

                <div className="field">
                  <label htmlFor="code">{t(parSecours ? 'admin.recovery.label' : 'admin.code.label')}</label>
                  <input
                    id="code"
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    autoComplete="one-time-code"
                    inputMode={parSecours ? 'text' : 'numeric'}
                    placeholder={parSecours ? 'XXXX-XXXX-XXXX-XXXX' : '123456'}
                    style={ltr}
                    autoFocus
                  />
                </div>

                {erreur}

                <button type="submit" disabled={pending} className="btn" style={{ justifyContent: 'center', width: '100%' }}>
                  {pending ? t('login.pending') : t('admin.code.submit')}
                </button>

                <div className="split" style={{ justifyContent: 'space-between' }}>
                  {etape.nom === 'verification' ? (
                    <button type="button" className="tbtn" onClick={() => { setParSecours(!parSecours); setCode('') }}>
                      {t(parSecours ? 'admin.verify.useApp' : 'admin.verify.useRecovery')}
                    </button>
                  ) : (
                    <span />
                  )}
                  <button type="button" className="tbtn" onClick={() => recommencer()}>
                    {t('admin.restart')}
                  </button>
                </div>
              </form>
            )}

            {etape.nom === 'secours' && (
              <div style={{ display: 'grid', gap: 11 }}>
                <h3 style={{ margin: 0, fontSize: 13.5, fontWeight: 600 }}>{t('admin.recovery.title')}</h3>
                <p className="mini" style={{ margin: 0 }}>{t('admin.recovery.intro')}</p>
                <ol data-testid="codes-secours" style={{ ...ltr, margin: 0, paddingInlineStart: 22, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 18px', fontSize: 13 }}>
                  {etape.resultat.recoveryCodes.map((c) => (
                    <li key={c}>{c}</li>
                  ))}
                </ol>
                <div className="split" style={{ justifyContent: 'space-between' }}>
                  <button type="button" className="tbtn" onClick={() => void copier(etape.resultat.recoveryCodes)}>
                    {t(copie ? 'admin.recovery.copied' : 'admin.recovery.copy')}
                  </button>
                  <button type="button" className="btn" onClick={() => adopt(etape.resultat.session)}>
                    {t('admin.recovery.saved')}
                  </button>
                </div>
              </div>
            )}

            <div className="split" style={{ justifyContent: 'center', gap: 6, marginTop: 2 }}>
              <LanguageToggle />
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}
